import history from '../../../../../history'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { DropdownItem } from '../../../../../Components/shared/Navbar/Dropdown/DropdownItem'
import { SvgWrapper } from '../../../../../Components/shared/Navbar/SvgWrapper'
import { Spinner } from '../../../../../Components/shared/Navbar/Spinner'
import { ListItem } from '../../../../../Components/shared/Navbar/ListItem'
import {
  FIND_A_HOME_DROPDOWN_TAB_NAMES,
  FROM,
  NAVIGATION_DELAY,
} from '../../../../../constants/navigation'
import { Text } from '../../../../../Components'
import { MarkerSvg, WalletSvg } from '../../../../../svgs'
import {
  selectClickedListItem,
  selectListItemLoading,
  setClickedListItem,
  setListItemLoading,
  setMobileNavIsOpen,
} from '../../../../../store/slices/navbar'
import {
  selectBuyingReportRoute,
  selectMarketExploreRoute,
} from '../../../../../store/selectors/routes'
import {
  selectActiveItem,
  selectHoShouldSeeBuyerReportNav,
  selectHoShouldSeeMarketsNav,
} from '../../../../../store/selectors/navigation'
import { selectClientIsGuest } from '../../../../../store/selectors/client'
import {
  setCreateClientFrom,
  SetCreateClientFromType,
} from '../../../../../actions/navigation'
import { toggleAuthModal } from '../../../../../actions/homeSearchAuthModal'

export interface SharedBuyerLinksProps {
  isMobile?: boolean
}

export const SharedBuyerLinks = ({ isMobile }: SharedBuyerLinksProps) => {
  const dispatch = useDispatch()
  const { BUYING_REPORT, EXPLORE_MARKETS } = FIND_A_HOME_DROPDOWN_TAB_NAMES

  const loading = useSelector(selectListItemLoading)
  const activeDropdownItem = useSelector(selectActiveItem)
  const clickedListItem = useSelector(selectClickedListItem)

  // Routes
  const buyingReportTo = useSelector(selectBuyingReportRoute)
  const marketsTo = useSelector(selectMarketExploreRoute)

  // Feature Gates
  const isGuest = useSelector(selectClientIsGuest)
  const showBuyersReportListItem = useSelector(selectHoShouldSeeBuyerReportNav)
  const showMarketExploreListItem = useSelector(selectHoShouldSeeMarketsNav)

  const processOnClick = (path: string, callback?: SetCreateClientFromType) => {
    if (isGuest) {
      isMobile && dispatch(setMobileNavIsOpen(false))

      setTimeout(() => {
        dispatch(toggleAuthModal(true))
      }, NAVIGATION_DELAY)

      return
    }

    if (callback) {
      dispatch(callback)
    }

    history.push(path)
  }

  const mappedKeyToRouteInfo = {
    [EXPLORE_MARKETS]: {
      handleOnClick: () => {
        return processOnClick(marketsTo, setCreateClientFrom(FROM.MARKET_TAB))
      },
    },
    [BUYING_REPORT]: {
      handleOnClick: () => processOnClick(buyingReportTo),
    },
  }

  const handleOnClick = key => {
    dispatch(setClickedListItem(key))
    dispatch(setListItemLoading(true))

    const listItemSpecificOnClick = mappedKeyToRouteInfo[key].handleOnClick

    setTimeout(() => {
      isMobile && dispatch(setMobileNavIsOpen(false))
      dispatch(setListItemLoading(false))
      listItemSpecificOnClick()
    }, NAVIGATION_DELAY)
  }

  const renderSvgOrLoading = (key, svg) => {
    if (loading && clickedListItem === key) {
      return <Spinner />
    }

    return <SvgWrapper>{svg}</SvgWrapper>
  }

  const Element = isMobile ? ListItem : DropdownItem

  return (
    <>
      {(isGuest || showMarketExploreListItem) && (
        <Element
          key={EXPLORE_MARKETS}
          isActive={activeDropdownItem === EXPLORE_MARKETS && !loading}
          onClick={() => handleOnClick(EXPLORE_MARKETS)}
        >
          {renderSvgOrLoading(EXPLORE_MARKETS, <MarkerSvg />)}
          <Text
            weight='semibold'
            element='span'
            variant='neutralWarm'
            variantCount='700'
            key={EXPLORE_MARKETS}
          >
            <FormattedMessage
              id='BuyerLinks.explore.button'
              defaultMessage='Explore markets'
            />
          </Text>
        </Element>
      )}
      {(isGuest || showBuyersReportListItem) && (
        <Element
          key={BUYING_REPORT}
          isActive={activeDropdownItem === BUYING_REPORT && !loading}
          onClick={() => handleOnClick(BUYING_REPORT)}
        >
          {renderSvgOrLoading(BUYING_REPORT, <WalletSvg />)}
          <Text
            weight='semibold'
            element='span'
            variant='neutralWarm'
            variantCount='700'
            key={BUYING_REPORT}
          >
            <FormattedMessage
              id='BuyerLinks.report.button'
              defaultMessage='Buying report'
            />
          </Text>
        </Element>
      )}
    </>
  )
}
